import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AuthContext } from '../../../AuthProvider';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import AnimatedDots from '../../AnimatedDots';
import { AI_AGENT_EMAIL } from '../../../llm/LlmChatView';
import { baseUrl_PB } from '../../../BaseUrl';
import { useAuth } from '../../../AuthProvider';

// Layout presentation components matching your upgraded UI framework
import UnifiedInputComposer from '../../UnifiedInputComposer';
import UnifiedMessageList from '../../UnifiedMessageList';
import UnifiedChatLoadingOverlay from '../../UnifiedChatLoadingOverlay';
import UnifiedLoadingHeads from '../../UnifiedLoadingHeads';
import SimpleConversationHeads from '../../SimpleConversationHeads';
import UnifiedChatHeader from '../../UnifiedChatHeader';

import './ChatView.css'; 

const SYSTEM_PROMPT = `
You are an electrical task structuring assistant.
Your role is to generate procedural step-by-step task sequences for standard electrical activities.
For this interaction, you must rely exclusively on the retrieved context provided to you.
CRITICAL RULE — CONTEXT ONLY
• You may ONLY use facts, procedures, standards, and instructions explicitly stated in the retrieved context.
...
“Draft Procedural Guidance – Requires Licensed Electrician Approval”
`;

const AWAITING_APPROVAL_TEXT = 'awaiting for approval of the response to your query';
const LEGACY_UNDECIDED_TEXT = 'it is not decided yet';
const GENERATING_RESPONSE_TEXT = 'Generating a response...';

const unifiedChatStyles = {};
export default function KbChatView(props) {
    const authContext = useAuth();
    const readingTrackRef = useRef(null);
    const timer = useRef(null);

    // Synchronized state engines
    const [allMessages, setAllMessages] = useState([]);
    const [conversationheads, setConversationheads] = useState([]);
    const [input, setInput] = useState("");
    const ai_agent = { other_user: 'Your assistant', other_user_email: AI_AGENT_EMAIL };
    const [otheruserref, setOtheruserref] = useState(String(ai_agent.other_user_email));
    const [convoId, setConvoId] = useState(null);
    const [uploadedImage, setUploadedImage] = useState(null); 
    const [loadingHeads, setLoadingHeads] = useState(false);
    const [loadingChat, setLoadingChat] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // State parameters from validation/approval modules
    const [pendingApprovalMeta, setPendingApprovalMeta] = useState(null);
    const [approvalRecords, setApprovalRecords] = useState([]);

    const userEmail = authContext?.additionalUserInfo?.email;

    // Original Web Markdown Formatting Preprocessor Engine
    function preprocessMarkdown(text) {
        if (!text) return "";
        let t = text;
        t = t.replace(/(#{1,6}\s*)([^\n]+)/g, "$1$2\n");
        t = t.replace(/(\d+\.\s)/g, "\n$1");
        t = t.replace(/(-\s)/g, "\n$1");
        t = t.replace(/\\n/g, "\n");
        return t;
    }

    
      useEffect(() => {
        if (convoId) {
          loadconversation(convoId, otheruserref);
        }
      }, [convoId]);

    // Sorting & Initial Layout Mounting Hook
    useEffect(() => {
        setAllMessages([]);
        setConvoId(null);
        setLoadingHeads(true);
        fetch(`${baseUrl_PB}loadconversationheads?user_email=${encodeURIComponent(userEmail)}`)
              .then(res => res.json())
              .then(data => {
                setConversationheads(data);
                setLoadingHeads(false);
              })
            .catch(() => {
                setLoadingHeads(false);
            });
    }, [authContext?.additionalUserInfo?.email]);

    // Polling Timer Configuration (4000ms loop updates)
    useEffect(() => {
        /*timer.current = setInterval(() => {
            if (convoId !== null) {
                loadconversation(convoId, otheruserref);
            }
        }, 4000);*/

        if (convoId === null) {
            clearInterval(timer.current);
        }
        return () => clearInterval(timer.current);
    }, [convoId, otheruserref]);

    const loadconversationheads = () => {
        setLoadingHeads(true);
        fetch(`${baseUrl_PB}/loadconversationheads?user_email=${authContext.additionalUserInfo.email}`)
            .then(response => response.json())
            .then(data => {
                data = data.map((convohead) => {
                    return { id: convohead.convoId, heading: "" + convohead.heading, chats: convohead.other_user };
                });
                setConversationheads(data);
                setLoadingHeads(false);
            })
            .catch((error) => {
                console.error('Error fetching conversation heads:', error);
                setLoadingHeads(false);
            });
    };

    const loadconversation = (targetConvoId, other_user) => {
        if (targetConvoId == null) return;
        if (Array.isArray(other_user)) {
            setOtheruserref(other_user[0] || "");
        } else {
            setOtheruserref(String(other_user));
        }
        setLoadingChat(true);
        fetch(`${baseUrl_PB}/loadconversation?convoId=${targetConvoId}`)
            .then(response => response.json())
            .then(data => {
                setAllMessages(data);
                setConvoId(data[0].convoId);
                setTimeout(() => {
                    setLoadingChat(false);
                }, 500);
            })
            .catch(() => {
                setLoadingChat(false);
            });
    };

    const newtask = () => {
        setAllMessages([]);
        setConvoId(null);
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploadedImage(file);
        console.log("Selected image mapping reference captured:", file);
    };

    // UNTOUCHED handleSend algorithm execution block tracking pipeline structures
    const handleSend = (e) => {
        if(e && e.preventDefault) e.preventDefault();
        if ((otheruserref != undefined && otheruserref.length > 0) || (allMessages[0] && allMessages[0].convoId == null)) {
            if (input.trim() === "") return;
            const inputmessage = { message_content: input, user_email: authContext.additionalUserInfo.email, ctimestamp: new Date().toISOString() };
            setAllMessages([...allMessages, inputmessage, { message_content: 'Checking...few moments please...', user_email: AI_AGENT_EMAIL, ctimestamp: new Date().toISOString() }]);
            const combinedQuery = input;
            const system_prompt = SYSTEM_PROMPT;
            let postData = { "query": combinedQuery, 'system_prompt': system_prompt, "user_email": authContext.additionalUserInfo.email, "other_user_email": otheruserref, "convoId": convoId }
            fetch(`${baseUrl_PB}/lms/`, {
                method: 'POST',
                body: JSON.stringify(postData),
            })
                .then(response => response.json())
                .then(data => {
                    if (data.convoId) {
                        setConvoId(data.convoId);
                        loadconversationheads();
                        loadconversation(data.convoId, otheruserref);
                    }
                    if (data.question) {
                        const newMessage = { message_content: data.question, user_email: AI_AGENT_EMAIL, ctimestamp: new Date().toISOString() };
                        setAllMessages([...allMessages, inputmessage, newMessage]);
                        loadconversationheads();
                        loadconversation(data.convoId, otheruserref);
                    }
                    setInput("");
                })
                .catch(() => {});
            setInput("");
        }
    };

    const isChatEmpty = (allMessages.length === 0);

    const filteredHeads = useMemo(() => {
        return conversationheads.filter(head => {
            if (!searchTerm.trim()) return true;
            const term = searchTerm.trim().toLowerCase();
            return (
                (head.heading && head.heading.toLowerCase().includes(term)) ||
                (head.chats && String(head.chats).toLowerCase().includes(term))
            );
        });
    }, [conversationheads, searchTerm]);

    useEffect(() => {
        if (!readingTrackRef.current) return;
        const scrollContainer = readingTrackRef.current.closest('.timeline-stream');
        if (scrollContainer) {
            scrollContainer.scrollTop = scrollContainer.scrollHeight;
        }
    }, [allMessages]);

    return (
        <div className="chat-container app-assist-view">
            
            <div className="sidebar">
                <button className="new-chat-btn" onClick={newtask}>
                    <span>+</span> New Guidance Session
                </button>

                <div className="recent-header">
                    <span>Guidance History</span>
                </div>
                
                <div className="search-wrapper">
                    <input
                        type="text"
                        className="search-input"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        placeholder="Search tasks..."
                    />
                </div>

                <div className="heads-container">
                    {loadingHeads ? (
                        <UnifiedLoadingHeads styles={{}} />
                    ) : (
                        <SimpleConversationHeads
                        
              styles={unifiedChatStyles}
              data={conversationheads.filter(head => {
                if (!searchTerm.trim()) return true;
                const term = searchTerm.trim().toLowerCase();
                return (
                  (head.heading && head.heading.toLowerCase().includes(term)) ||
                  (head.other_user && head.other_user.toLowerCase().includes(term))
                );
              })}
              onSelect={item => setConvoId(item.convoId || item.id)}
                        />
                    )}
                </div>
            </div>

            <div className={`chat-content ${isChatEmpty ? 'is-empty' : ''}`}>
                
                <UnifiedChatHeader
                    styles={{}}
                    title=""
                    subtitle=""
                />

                <div className="timeline-stream">
                    {loadingChat && <UnifiedChatLoadingOverlay styles={{}} />}
                    
                    <div className="reading-track" ref={readingTrackRef}>
                        <UnifiedMessageList
                            styles={{}}
                            data={allMessages}
                            currentUserEmail={userEmail || 'You'}
                            renderMessageContent={msg => {
                                return (
                                    <div className="markdown-content">
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                            {preprocessMarkdown(msg.message_content)}
                                        </ReactMarkdown>
                                    </div>
                                );
                            }}
                            selectableText={true}
                        />
                    </div>
                </div>

                <div className="action-hub">
                    {isChatEmpty && (
                        <div className="landing-welcome-hero">
                            <h1>How can I help today?</h1>
                        </div>
                    )}
                 
                    <div className="input-max-width">
                        {uploadedImage && (
                            <div className="upload-pills">
                                <div className="file-preview">
                                    <span className="file-name">📎 {uploadedImage.name} loaded</span>
                                </div>
                            </div>
                        )}
                        
                        <div className="input-wrapper">
                            <UnifiedInputComposer
                                styles={{}}
                                value={input}
                                onChangeText={setInput}
                                onSend={handleSend}
                                placeholder="Ask about step sequences, PPE, or isolation guidelines..."
                                showAttachment={true}
                                onAttachmentPress={() => document.getElementById('hidden-file-upload').click()}
                                sendDisabled={!input.trim()}
                            />
                            <input
                                id="hidden-file-upload"
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                style={{ display: 'none' }}
                            />
                        </div>
                        
                        <div className="footer-disclaimer">
                            Effiez Assistant can make mistakes. Verify important information.
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}